import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Primary, Secondary, Sheet } from "@/components/backoffice/kit";
import GenericProductForm from "@/components/backoffice/GenericProductForm";

/** 8.5 Add product: From 13thav catalog (multi-select grid, Add (n)) or My own product (the generic form). */
export default function AddProductSheet({ onAdded, onClose, isHome = false }) {
    const [mode, setMode] = useState(null);
    const [lib, setLib] = useState(null);
    const [picked, setPicked] = useState([]);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (mode !== "catalog") return;
        api.get("/store/catalog/library").then(({ data }) => setLib(data.templates.filter((t) => !t.added)))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    }, [mode]);
    const toggle = (t) => setPicked((p) => (p.some((x) => x.slug === t.slug) ? p.filter((x) => x.slug !== t.slug) : [...p, t]));
    const add = async () => {
        setBusy(true);
        try {
            for (const t of picked) await api.post("/store/catalog/add", { kind: t.kind, slug: t.slug });
            toast.success(`${picked.length} product${picked.length === 1 ? "" : "s"} added`);
            onAdded();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <Sheet title={mode === "catalog" ? "From 13thav catalog" : mode === "own" ? "My own product" : "Add product"} onClose={onClose} testid="add-product-sheet">
            {!mode && (
                <div className="grid gap-2">
                    {!isHome && <Primary onClick={() => setMode("catalog")} testid="add-from-catalog" full>From 13thav catalog</Primary>}
                    <Secondary onClick={() => setMode("own")} testid="add-own" full>My own product</Secondary>
                </div>
            )}
            {mode === "catalog" && (
                lib === null ? <div className="flex min-h-[20vh] items-center justify-center"><Loader2 className="animate-spin" /></div>
                    : lib.length === 0 ? <p className="text-[15px]" data-testid="library-empty">Your shop already carries every 13thav product.</p>
                        : (
                            <>
                                <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="library-grid">
                                    {lib.map((t) => {
                                        const on = picked.some((x) => x.slug === t.slug);
                                        return (
                                            <li key={`${t.kind}-${t.slug}`}>
                                                <button onClick={() => toggle(t)} aria-pressed={on} data-testid={`library-${t.slug}`}
                                                    className={`block w-full border-2 bg-white p-2 text-left ${on ? "border-[var(--accent)]" : "border-[#767676]"}`}>
                                                    <div className="h-16 w-full">{t.image && <img src={t.image} alt="" className="h-full w-full object-contain" />}</div>
                                                    <p className="mt-1 truncate text-[13px] font-semibold">{t.name}</p>
                                                    <p className="text-[12px] text-[#4d4d4d]">from ${Number(t.from_price || 0).toFixed(2)}</p>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <div className="mt-3"><Primary onClick={add} disabled={!picked.length || busy} testid="library-add" full>Add ({picked.length})</Primary></div>
                            </>
                        )
            )}
            {mode === "own" && (
                <>
                    {!isHome && <p className="mb-3 text-[14px] text-[#4d4d4d]" data-testid="own-note">Your own product type: you print and fulfill it — 13thav has no master copy to print from.</p>}
                    <GenericProductForm onSaved={onAdded} showFulfillment={false} />
                </>
            )}
        </Sheet>
    );
}
